import { useState } from "react";
import axios from "axios";
import { Heart, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [50, 100, 150, 200];

export default function DonateButton() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const finalAmount = custom ? parseFloat(custom.replace(",", ".")) : amount;
  const valid = Number.isFinite(finalAmount) && finalAmount >= 1 && finalAmount <= 1000;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!valid) {
      setErr("Betrag muss zwischen 1 € und 1000 € liegen.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/donations/checkout`,
        {
          amount_eur: finalAmount,
          origin_url: window.location.origin,
          message: message.trim() || null,
        },
        { withCredentials: true }
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setErr("Checkout konnte nicht gestartet werden.");
        setLoading(false);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Fehler beim Starten der Spende.");
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="header-donate-btn"
        title="Plattform freiwillig unterstützen — Mitgliedsbeitrag mit Rechnung"
        className="group flex items-center gap-1.5 px-2 md:px-3 h-9 text-[12.5px] font-outfit font-semibold tracking-tight text-[#00e5ff] border border-[#00e5ff]/50 bg-[#00e5ff]/[0.08] hover:bg-[#00e5ff]/20 hover:border-[#00e5ff] hover:shadow-[0_0_20px_-4px_rgba(0,229,255,0.5)] transition-all duration-200"
      >
        <Heart size={14} className="group-hover:fill-[#00e5ff]/40 transition-all" />
        <span className="hidden md:inline">Spenden</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setErr(""); setLoading(false); } setOpen(v); }}>
        <DialogContent
          className="bg-transparent border-0 text-white rounded-none max-w-md p-0 shadow-none"
          data-testid="donate-dialog"
        >
          <div className="relative">
            <div className="absolute -inset-px pointer-events-none"
              style={{ background: "linear-gradient(135deg, #00e5ff40 0%, transparent 50%, #00e5ff20 100%)" }} />
            <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-px bg-[#00e5ff]/60" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">
                    Mitgliedsbeitrag
                  </span>
                </div>
                <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">
                  [KI]NPUT Board unterstützen
                </DialogTitle>
                <DialogDescription className="text-[13px] text-white/45 mt-1 leading-relaxed">
                  Freiwilliger Mitgliedsbeitrag für die Community-Plattform.
                  Du erhältst eine ordentliche Rechnung (inkl. MwSt.) per E-Mail.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4 mt-5">
                <div className="grid grid-cols-4 gap-2">
                  {PRESETS.map((v) => {
                    const active = !custom && amount === v;
                    return (
                      <button
                        type="button"
                        key={v}
                        data-testid={`donate-preset-${v}`}
                        onClick={() => { setAmount(v); setCustom(""); }}
                        className={`h-11 font-outfit font-semibold text-[14px] border transition-all ${
                          active
                            ? "bg-[#00e5ff] text-black border-[#00e5ff]"
                            : "bg-white/[0.02] border-white/10 text-white/80 hover:border-[#00e5ff]/50"
                        }`}
                      >
                        {v} €
                      </button>
                    );
                  })}
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-semibold">
                    Oder eigener Betrag (€)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    step="0.01"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="z. B. 15"
                    data-testid="donate-custom-amount"
                    className="mt-2 bg-white/[0.02] border-white/10 text-white h-11 rounded-none focus-visible:ring-[#00e5ff] focus-visible:ring-1 focus-visible:border-[#00e5ff]/60"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-outfit font-semibold">
                    Nachricht (optional)
                  </label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Was motiviert dich zur Unterstützung?"
                    maxLength={500}
                    rows={3}
                    data-testid="donate-message"
                    className="mt-2 bg-white/[0.02] border-white/10 text-white rounded-none focus-visible:ring-[#00e5ff] focus-visible:ring-1 focus-visible:border-[#00e5ff]/60 resize-none"
                  />
                </div>

                <div className="border-l-2 border-[#00e5ff]/40 pl-3 text-[13px] text-white/60">
                  Dein Beitrag: <span className="text-white font-semibold">
                    {valid ? finalAmount.toFixed(2).replace(".", ",") : "—"} €
                  </span>
                  <span className="text-white/40"> (inkl. 19 % MwSt.)</span>
                </div>

                {err && <p className="text-[13px] text-red-400" data-testid="donate-error">{err}</p>}

                <Button
                  type="submit"
                  disabled={!valid || loading}
                  data-testid="donate-submit-btn"
                  className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all disabled:opacity-40"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin mr-2" /> Weiterleitung zu Stripe…</>
                  ) : (
                    <>Sicher zahlen mit Stripe</>
                  )}
                </Button>
                <p className="text-[11px] text-white/35 text-center leading-snug">
                  Zahlung via Stripe · Kreditkarte, SEPA, Klarna, Apple&nbsp;Pay, Google&nbsp;Pay
                </p>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
